import type React from "react";
import { createContext, useState, useContext, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { getAllPages } from "../services/apiServices";

export interface Page {
	id: string;
	title: string;
	children: Page[];
	position: number;
}

const sortByPosition = (pages: Page[]) => {
	pages.sort((a, b) => a.position - b.position);
	for (const page of pages) {
		if (page.children && page.children.length > 0) {
			sortByPosition(page.children);
		}
	}
};

// Define the context type
interface PageContextType {
	selectedPageId: string | null;
	setSelectedPageId: (id: string) => void;
	selectedPageContent: string | null;
	setSelectedPageContent: (id: string) => void;
	selectedHeaderIndex: number | null;
	setSelectedHeaderIndex: (id: number) => void;
	headerLogo: string | null;
	setHeaderLogo: (id: string) => void;
	userAvatar: string | null;
	setUserAvatar: (id: string) => void;
	pages: Page[];
	pagesLoading: boolean;
}

// Create context
const PageContext = createContext<PageContextType | undefined>(undefined);

// Provider component
export const PageProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [selectedPageId, setSelectedPageId] = useState<string | null>(
		localStorage.getItem("selectedPageId") || null,
	);
	const [selectedPageContent, setSelectedPageContent] = useState<string | null>(
		null,
	);
	const [selectedHeaderIndex, setSelectedHeaderIndex] = useState<number | null>(
		1,
	);
	const [headerLogo, setHeaderLogo] = useState<string | null>(null);
	const [userAvatar, setUserAvatar] = useState<string | null>(null);
	const [pages, setPages] = useState<Page[]>([]);
	const [pagesLoading, setPagesLoading] = useState(true);
	const { isAuthenticated } = useAuth();

	// Fetched once here instead of once each in Header and Sidebar — both
	// used to independently re-run this same paginated fetch on every mount.
	useEffect(() => {
		let cancelled = false;

		const fetchPages = async () => {
			setPagesLoading(true);
			try {
				const pageListArray: Page[] = await getAllPages(isAuthenticated);
				sortByPosition(pageListArray);
				const pageArray =
					pageListArray.length > 1
						? [...pageListArray[0].children, ...pageListArray[1].children]
						: (pageListArray[0]?.children ?? []);
				if (!cancelled) setPages(pageArray);
			} catch (error) {
				console.error("Error fetching pages:", error);
			} finally {
				if (!cancelled) setPagesLoading(false);
			}
		};

		fetchPages();
		return () => {
			cancelled = true;
		};
	}, [isAuthenticated]);

	return (
		<PageContext.Provider
			value={{
				selectedPageId,
				setSelectedPageId,
				selectedPageContent,
				setSelectedPageContent,
				headerLogo,
				setHeaderLogo,
				userAvatar,
				setUserAvatar,
				selectedHeaderIndex,
				setSelectedHeaderIndex,
				pages,
				pagesLoading,
			}}
		>
			{children}
		</PageContext.Provider>
	);
};

// Custom hook to use the context
export const usePageContext = () => {
	const context = useContext(PageContext);
	if (!context) {
		throw new Error("usePageContext must be used within a PageProvider");
	}
	return context;
};
